import CardTitleWithIcon from "@/components/company/card-title-with-icon";
import { CardLoadingWrapper } from "@/components/company/company-animations";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useTranslation } from "@/hooks/useTranslation";
import {
  GlobeIcon,
  PackageIcon,
  SparklesIcon,
  TagIcon,
  UsersIcon,
} from "lucide-react";

const OFFER_SECTION_ICONS: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  "produits/services": PackageIcon,
  "products/services": PackageIcon,
  "proposition de valeur": SparklesIcon,
  "value proposition": SparklesIcon,
  "cible clientèle": UsersIcon,
  "target audience": UsersIcon,
  "target customers": UsersIcon,
  "marché visé": GlobeIcon,
  "marché cible": GlobeIcon,
  marché: GlobeIcon,
  "zone géographique": GlobeIcon,
  "target market": GlobeIcon,
  "target geography": GlobeIcon,
  positionnement: SparklesIcon,
  positioning: SparklesIcon,
};

const OFFER_SECTION_TITLE_REMAP: Record<string, string> = {
  "marché visé": "Zone géographique",
  "marché cible": "Zone géographique",
  marché: "Zone géographique",
  "target market": "Target geography",
  positionnement: "Différenciation",
  positioning: "Differentiation",
};

type OfferSection = {
  title: string;
  content: string;
  Icon: React.ComponentType<{ className?: string }>;
};

function renderBoldText(text: string): React.ReactNode[] {
  const parts = text.split(/\*\*([^*]+)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : part,
  );
}

function parseOfferSummary(offerSummary: string): OfferSection[] {
  const sections: OfferSection[] = [];
  const regex = /\*\*([^*]+)\*\*\s*:?\s*/g;
  const matches = [...offerSummary.matchAll(regex)];

  for (let i = 0; i < matches.length; i++) {
    const match = matches[i];
    const title = match[1].trim();
    const startIndex = match.index! + match[0].length;
    const endIndex =
      i + 1 < matches.length ? matches[i + 1].index! : offerSummary.length;
    const rawContent = offerSummary.slice(startIndex, endIndex).trim();
    const content = rawContent
      .replace(/^-\s*/, "")
      .replace(/\s*-\s*$/g, "")
      .replace(/\s*-\s+/g, ", ")
      .trim();

    if (!content) continue;

    const normalizedTitle = title.toLowerCase().replace(/\s+/g, " ").trim();
    const Icon = OFFER_SECTION_ICONS[normalizedTitle] ?? TagIcon;
    const displayTitle = OFFER_SECTION_TITLE_REMAP[normalizedTitle] ?? title;

    const existing = sections.find((s) => s.title === displayTitle);
    if (existing) {
      existing.content = `${existing.content}\n${content}`;
    } else {
      sections.push({ title: displayTitle, content, Icon });
    }
  }

  return sections;
}

type CardOfferSummaryProps = {
  offerSummary: string;
  isLoading?: boolean;
};

export default function CardOfferSummary({
  offerSummary,
  isLoading = false,
}: CardOfferSummaryProps) {
  const { t } = useTranslation();
  const parsed = parseOfferSummary(offerSummary);
  // A summary with no section title is a source's free text (Coresignal's
  // LinkedIn description): one card rather than none.
  const sections =
    parsed.length > 0 || !offerSummary.trim()
      ? parsed
      : [
          {
            title: t("company.insights.offerSummary"),
            content: offerSummary.trim(),
            Icon: PackageIcon,
          },
        ];

  if (sections.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-4 @2xl:grid-cols-2 sm:gap-5">
      {sections.map((section) => (
        <CardLoadingWrapper
          key={section.title}
          isLoading={isLoading}
          className="h-full"
        >
          <Card className="h-full flex flex-col min-w-0 overflow-hidden">
            <CardHeader>
              <CardTitleWithIcon icon={section.Icon} title={section.title} />
            </CardHeader>
            <CardContent>
              <p className="text-sm leading-relaxed text-foreground whitespace-pre-line">
                {renderBoldText(section.content)}
              </p>
            </CardContent>
          </Card>
        </CardLoadingWrapper>
      ))}
    </div>
  );
}
